import { useEffect, useMemo, useState } from "react";
import { type WorkBook, read, utils } from "xlsx";

import { SpreadsheetViewerView } from "./SpreadsheetViewerView";
import { type SpreadsheetViewerProps } from "./types";

export function SpreadsheetViewer({ fileUrl, fileName }: SpreadsheetViewerProps) {
  const [workbook, setWorkbook] = useState<WorkBook | null>(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setWorkbook(null);
    setHasLoadError(false);
    setActiveSheetIndex(0);

    const loadWorkbook = async (): Promise<void> => {
      try {
        const response = await fetch(fileUrl);
        // SheetJS parses almost any bytes, so an error page body would render as a bogus sheet.
        if (!response.ok) throw new Error(`Spreadsheet fetch failed: ${response.status}`);
        const buffer = await response.arrayBuffer();
        const parsed = read(buffer, { type: "array" });
        if (!cancelled) setWorkbook(parsed);
      } catch (error) {
        console.error("Error rendering spreadsheet:", error);
        if (!cancelled) setHasLoadError(true);
      }
    };

    loadWorkbook();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  const sheetNames = workbook?.SheetNames ?? [];

  const rows = useMemo(() => {
    const sheetName = workbook?.SheetNames[activeSheetIndex];
    const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;
    if (!sheet) return [];
    // raw: false yields the cell's displayed text, so dates don't render as serial numbers.
    return utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false });
  }, [workbook, activeSheetIndex]);

  const columnCount = useMemo(
    () => rows.reduce((max, row) => Math.max(max, row.length), 0),
    [rows]
  );

  return (
    <SpreadsheetViewerView
      fileName={fileName}
      isLoading={!workbook && !hasLoadError}
      hasLoadError={hasLoadError}
      sheetNames={sheetNames}
      activeSheetIndex={activeSheetIndex}
      rows={rows}
      columnCount={columnCount}
      onSheetChange={setActiveSheetIndex}
    />
  );
}
